import { Api, ApiEnums, ApiTypes, QueryOf } from '@app/api';
import { PayloadForFoodbacks } from '@core/interfaces/payload-for-foodbacks.interface';
import {
  IQuestionAllAnswerFromBackend,
  IQuestionStatsAllAnswersPayload,
} from '@shared/components/questions-stats/interfaces/questions-stats.interface';

export type AccountFoodbacksQuery = QueryOf<
  typeof Api.surveys.analytics.accounts.foodbacks.paged,
  'post'
>;
export type AccountFoodbacksFilters = Api.surveys.analytics.accounts.foodbacks.paged.PostBody;
export type VenueFoodbacksQuery = QueryOf<typeof Api.surveys.analytics.venues.foodbacks.paged>;

export type AccountStatementFoodbacksFilters =
  Api.surveys.analytics.accounts.statements.foodbacks.paged.PostBody;
export type VenueStatementFoodbacksQuery = QueryOf<
  typeof Api.surveys.analytics.venues.statements.foodbacks.paged
>;

export type AccountCampaignAnswersFilters =
  Api.surveys.analytics.accounts.campaign.question.allAnswers.paged.PostBody;
export type VenueAnswersQuery = QueryOf<
  typeof Api.surveys.analytics.venues.oneMoreQuestion.allAnswers.paged
>;

export type AnswerRow = ApiTypes.AnswerRowListItemResponseDto;

/** Paging only: the analytics lists that neither sort nor search take nothing else in the query. */
export interface ListPage {
  page: number;
  pageSize: number;
}

export interface AccountListRequest<Query, Filters> {
  query: Query;
  body: Filters;
}

type FoodbacksSortField = NonNullable<VenueFoodbacksQuery['sort']>[number];

/** The columns the foodbacks lists sort by; anything else answers 422, so it falls back to the default. */
export const SORTABLE_FOODBACK_FIELDS = [
  'lastModified',
  'overallExperienceAverage',
  'comment',
  'name',
  'email',
  'ageGroup',
  'gender',
  'lang',
  'zipCode',
  'venueName',
] as const satisfies readonly FoodbacksSortField[];

/** The foodbacks lists refuse a search outside these lengths with a 422. */
const SEARCH_MIN_LENGTH = 3;
const SEARCH_MAX_LENGTH = 100;

const AGE_GROUPS = [
  '<=12',
  '13-17',
  '18-24',
  '25-29',
  '30-39',
  '40-49',
  '50-59',
  '60-69',
  '70-79',
  '80-89',
  '>=90',
] as const satisfies readonly ApiTypes.AgeGroup[];

const LANGUAGES: readonly string[] = Object.values(ApiEnums.LanguageEnum);

export function toAccountFoodbacksRequest(
  payload: PayloadForFoodbacks
): AccountListRequest<AccountFoodbacksQuery, AccountFoodbacksFilters> {
  return {
    query: foodbacksPage(payload),
    body: { ...foodbacksFilters(payload), ...unitsFilter(payload.venues) },
  };
}

/** A unit's list has no unit filter: it is already narrowed to the unit in the path. */
export function toVenueFoodbacksQuery(payload: PayloadForFoodbacks): VenueFoodbacksQuery {
  return { ...foodbacksPage(payload), ...foodbacksFilters(payload) };
}

export function toAccountStatementFoodbacksRequest(
  payload: PayloadForFoodbacks
): AccountListRequest<ListPage, AccountStatementFoodbacksFilters> {
  return {
    query: listPage(payload.page, payload.limit),
    body: {
      ...statementFoodbacksFilters(payload),
      ...unitsFilter(payload.venues),
      ...(payload['venueGroups[]']?.length ? { venueGroups: payload['venueGroups[]'] } : {}),
    },
  };
}

export function toVenueStatementFoodbacksQuery(
  payload: PayloadForFoodbacks
): VenueStatementFoodbacksQuery {
  return { ...listPage(payload.page, payload.limit), ...statementFoodbacksFilters(payload) };
}

/** `optionUuids` is a campaign filter, and only the account route takes it. */
export function toAccountAnswersRequest(
  payload: IQuestionStatsAllAnswersPayload
): AccountListRequest<ListPage, AccountCampaignAnswersFilters> {
  return {
    query: listPage(payload.paginate.page, payload.paginate.limit),
    body: {
      ...answersFilters(payload),
      ...unitsFilter(payload.uuids),
      ...(payload.optionUuids?.length ? { optionUuids: payload.optionUuids } : {}),
    },
  };
}

export function toVenueAnswersQuery(payload: IQuestionStatsAllAnswersPayload): VenueAnswersQuery {
  return { ...listPage(payload.paginate.page, payload.paginate.limit), ...answersFilters(payload) };
}

/**
 * The answer lists page by answer row, so one response's rows can straddle two pages. Rows arrive
 * in response order; this gathers each response's rows into one group, keeping that order.
 */
export function groupAnswersByResponse(
  rows: readonly AnswerRow[]
): IQuestionAllAnswerFromBackend[][] {
  const groups = new Map<string, IQuestionAllAnswerFromBackend[]>();

  for (const row of rows) {
    const group = groups.get(row.responseUuid) ?? [];
    group.push(row);
    groups.set(row.responseUuid, group);
  }

  return [...groups.values()];
}

/**
 * Splits off the last response of a page that is not the last one, since its remaining rows may be
 * on the next page. Pass the held-back rows in front of the next page's rows. A page that is all
 * one response is passed through whole, so there is always something to show.
 */
export function holdBackOpenResponse(
  rows: readonly AnswerRow[],
  isLastPage: boolean
): { complete: AnswerRow[]; heldBack: AnswerRow[] } {
  const lastResponseUuid = rows.at(-1)?.responseUuid;

  if (
    isLastPage ||
    lastResponseUuid === undefined ||
    rows.every(row => row.responseUuid === lastResponseUuid)
  ) {
    return { complete: [...rows], heldBack: [] };
  }

  return {
    complete: rows.filter(row => row.responseUuid !== lastResponseUuid),
    heldBack: rows.filter(row => row.responseUuid === lastResponseUuid),
  };
}

function listPage(page: number, pageSize: number): ListPage {
  return { page, pageSize };
}

function foodbacksPage(payload: PayloadForFoodbacks): AccountFoodbacksQuery {
  const sort = payload.sort;
  const field = sort?.field;
  const search = payload.commentContains?.trim() ?? '';

  return {
    ...listPage(payload.page, payload.limit),
    ...(sort && isSortableFoodbackField(field)
      ? { sort: [sort.ascending ? field : (`-${field}` as const)] }
      : {}),
    ...(search.length >= SEARCH_MIN_LENGTH ? { q: search.slice(0, SEARCH_MAX_LENGTH) } : {}),
  };
}

/**
 * The screens send `false` or null for a flag they do not filter on, but on these lists `false`
 * asks for the opposite (no comment, no e-mail), so only a set flag is passed on.
 */
function foodbacksFilters(payload: PayloadForFoodbacks) {
  return {
    ...dateRange(payload.dateFrom, payload.dateTo),
    ...(payload.hasComment ? { hasComment: true } : {}),
    ...(payload.hasEmail ? { hasEmail: true } : {}),
    ...(payload.isContactAllowed ? { isContactAllowed: true } : {}),
    ...(payload.flags?.length ? { flags: [...payload.flags] } : {}),
  };
}

function statementFoodbacksFilters(payload: PayloadForFoodbacks) {
  return {
    ...dateRange(payload.dateFrom, payload.dateTo),
    ...(payload.hasComment ? { hasComment: true } : {}),
  };
}

function answersFilters(payload: IQuestionStatsAllAnswersPayload) {
  const ageGroup = payload.demographics?.ageGroup?.filter(isAgeGroup) ?? [];
  const gender = payload.demographics?.gender ?? [];
  const lang = payload.demographics?.lang?.filter(isLanguage) ?? [];

  return {
    ...dateRange(payload.between?.dateFrom, payload.between?.dateTo),
    ...(ageGroup.length ? { ageGroup } : {}),
    ...(gender.length ? { gender } : {}),
    ...(lang.length ? { lang } : {}),
  };
}

function dateRange(dateFrom: string | null | undefined, dateTo: string | null | undefined) {
  return {
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  };
}

/** No unit list means every unit the account has. */
function unitsFilter(venues: readonly string[] | null | undefined): { venues?: string[] } {
  return venues?.length ? { venues: [...venues] } : {};
}

function isSortableFoodbackField(
  field: string | undefined
): field is (typeof SORTABLE_FOODBACK_FIELDS)[number] {
  return (SORTABLE_FOODBACK_FIELDS as readonly string[]).includes(field ?? '');
}

function isAgeGroup(value: string): value is ApiTypes.AgeGroup {
  return (AGE_GROUPS as readonly string[]).includes(value);
}

function isLanguage(value: string): value is ApiTypes.LanguageEnum {
  return LANGUAGES.includes(value);
}
